import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loader2, SearchCheck } from "lucide-react";
import { api } from "@/api";
import { useLang } from "@/i18n";
import { Reveal } from "@/components/Reveal";
import { Seo } from "@/components/Seo";

export default function BookingStatus() {
    const { lang, t } = useLang();
    const [params] = useSearchParams();
    const [code, setCode] = useState(params.get("code") || "");
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [notFound, setNotFound] = useState(false);

    const check = async (e) => {
        e.preventDefault();
        setLoading(true);
        setResult(null);
        setNotFound(false);
        try {
            const { data } = await api.get("/public/booking-status", { params: { code: code.trim(), email: email.trim() } });
            setResult(data);
        } catch {
            setNotFound(true);
        } finally {
            setLoading(false);
        }
    };

    const STATUS_COLOR = {
        pending_confirmation: "text-amber-300 border-amber-500/40 bg-amber-400/10",
        confirmed: "text-emerald-300 border-emerald-500/40 bg-emerald-400/10",
        cancelled: "text-red-300 border-red-500/40 bg-red-400/10",
    };

    return (
        <div data-testid="booking-status-page" className="marble-bg min-h-screen pt-32 pb-24">
            <Seo lang={lang} title={`${t.statusPage.title} — TOURVOYAGERS`} description={t.statusPage.sub} />
            <div className="max-w-xl mx-auto px-5">
                <Reveal>
                    <p className="text-xs uppercase tracking-[0.3em] text-amber-400/90 font-medium">TOURVOYAGERS</p>
                    <h1 className="mt-4 font-display text-4xl sm:text-5xl text-amber-50 leading-tight">{t.statusPage.title}</h1>
                    <p className="mt-4 text-base text-stone-300">{t.statusPage.sub}</p>
                </Reveal>

                <Reveal delay={0.1}>
                    <form onSubmit={check} className="mt-10 gold-card rounded-xl p-8 grid gap-4" data-testid="status-form">
                        <label className="block">
                            <span className="text-[11px] uppercase tracking-widest text-amber-500/80">{t.statusPage.codeLabel}</span>
                            <input data-testid="status-code-input" className="input-lux mt-1 font-mono-accent" placeholder={t.statusPage.codePlaceholder} value={code} onChange={(e) => setCode(e.target.value)} required />
                        </label>
                        <label className="block">
                            <span className="text-[11px] uppercase tracking-widest text-amber-500/80">{t.statusPage.emailLabel}</span>
                            <input data-testid="status-email-input" type="email" className="input-lux mt-1" value={email} onChange={(e) => setEmail(e.target.value)} required />
                        </label>
                        <button data-testid="status-check-button" disabled={loading || !code.trim() || !email.includes("@")} className="mt-2 inline-flex items-center justify-center gap-2 rounded-full bg-amber-400 px-8 py-3.5 text-xs font-bold uppercase tracking-[0.16em] text-black transition-all hover:bg-amber-300 disabled:opacity-40">
                            {loading ? <Loader2 size={14} className="animate-spin" /> : <SearchCheck size={14} />}
                            {loading ? t.statusPage.checking : t.statusPage.check}
                        </button>
                    </form>
                </Reveal>

                {notFound && (
                    <div className="mt-6 rounded-xl border border-red-500/30 bg-red-400/5 p-5 text-sm text-red-300" data-testid="status-not-found">
                        {t.statusPage.notFound}
                    </div>
                )}

                {result && (
                    <Reveal>
                        <div className="mt-6 gold-card rounded-xl p-8" data-testid="status-result">
                            <p className="text-[10px] uppercase tracking-[0.3em] text-amber-500/80">{t.statusPage.codeLabel}</p>
                            <p className="mt-1 font-mono-accent text-2xl text-amber-300" data-testid="status-result-code">{result.code}</p>
                            <div className="mt-5 space-y-3 text-sm">
                                <div className="flex justify-between border-b border-amber-500/10 pb-3">
                                    <span className="text-stone-400">{t.statusPage.packageLabel}</span>
                                    <span className="text-stone-200 text-right">{result.package_name || "—"}</span>
                                </div>
                                <div className="flex justify-between border-b border-amber-500/10 pb-3">
                                    <span className="text-stone-400">{t.statusPage.dateLabel}</span>
                                    <span className="text-stone-200">{result.date} {result.time}</span>
                                </div>
                                <div className="flex justify-between items-center">
                                    <span className="text-stone-400">{t.payment.statusLabel}</span>
                                    <span data-testid="status-result-badge" className={`rounded-full border px-3 py-1 text-[11px] font-semibold uppercase tracking-wider ${STATUS_COLOR[result.status] || "text-stone-300 border-stone-500/40"}`}>
                                        {t.status[result.status] || result.status}
                                    </span>
                                </div>
                            </div>
                            <p className="mt-5 text-xs text-stone-500 leading-relaxed" data-testid="status-result-note">
                                {result.registered || t.statusPage.registered} {result.live ? t.statusPage.liveNote : t.statusPage.lastKnownNote}
                            </p>
                        </div>
                    </Reveal>
                )}
            </div>
        </div>
    );
}
